import cn from 'classnames'
import { JSX, ReactNode, useLayoutEffect, useRef, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { TPopupHorizontal, TPopupVertical } from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'

import styles from './TabStrip.module.scss'

export interface ITabStripItem<TKey extends string = string> {
  key: TKey
  icon?: ReactNode
  title: ReactNode
  secondaryContent?: ReactNode
  subtitle?: ReactNode
  subtitleColor?: string
  adornment?: ReactNode
  /** Drawn in the tab's top-right corner, clear of the title row — the act on the tab itself,
   *  such as dropping the programme from the cockpit. */
  cornerAction?: ReactNode
  muted?: boolean
  /** On the segmented strip a muted tab also wears a crossed eye; this names it for assistive
   *  technology and on hover. */
  mutedLabel?: string
  /** Something is being done to what the tab holds: on the segmented strip a spinner stands in
   *  the place of the tone dot or the crossed eye until it is done. */
  loading?: boolean
  disabled?: boolean
  /** A coloured fold in the tab's top-left corner: the state of what the tab holds, stated
   *  without text so the tab keeps to its icon and title. */
  tone?: 'success' | 'caution' | 'warning' | 'danger'
  /** On the segmented strip a count above zero turns the tone dot into a pulsing badge that
   *  carries it. With no count and no tone but success the tab wears a green check instead. */
  count?: number
  /** What the tab holds is not finished yet: in place of the green check it wears an orange disc
   *  with an exclamation mark, as nothing raised is not a clean bill then. */
  unfinished?: boolean
  /** Shown under the tab on hover: a short preview of what selecting it would bring up. */
  tooltip?: () => JSX.Element
}

export interface ITabStripProps<TKey extends string = string> {
  items: ITabStripItem<TKey>[]
  selectedKey: TKey
  onSelect: (key: TKey) => void
  className?: string
  /** `underline` marks the selected tab with a rule under it; `filled` tints its background
   *  instead and keeps the row free of horizontal lines. `surface` draws nothing behind the tabs:
   *  the selected and hovered shapes come from a layer the strip sits on. `segmented` sets the tabs
   *  in a track with a white thumb that slides to the selected one; the tone is a dot after the
   *  title instead of a fold. */
  appearance?: 'underline' | 'filled' | 'surface' | 'segmented'
  /** Drawn inside the strip after the last tab: an act on the tabs themselves, such as the setting
   *  of which ones there are. It is not a tab and takes no part in the selection. */
  trailing?: ReactNode
}

interface IThumbPlacement {
  left: number
  width: number
}

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M4.5 8.2l2.3 2.3 4.7-4.9" />
  </svg>
)

const ExclamationIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M8 4.2v4.6M8 11.5v.1" />
  </svg>
)

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M2.5 12S6 5.5 12 5.5c1.6 0 3 .4 4.2 1M21.5 12s-1.3 2.4-3.7 4.3M14.3 17.9c-.7.2-1.5.4-2.3.4-6 0-9.5-6.5-9.5-6.5" />
    <path d="M4 4l16 16" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
)

/** Where the segmented strip's thumb stands: over the selected tab, measured from the strip so a
 *  tooltip wrapped around a tab does not shift it. It follows the tab through resizes and lands
 *  without a slide the first time, so the page does not open on a moving thumb. */
const useThumbPlacement = (isSegmented: boolean, selectedKey: string, itemsCount: number) => {
  const stripRef = useRef<HTMLDivElement>(null)
  const [placement, setPlacement] = useState<IThumbPlacement | null>(null)
  const [isSliding, setIsSliding] = useState(false)

  useLayoutEffect(() => {
    const strip = stripRef.current

    if (!isSegmented || !strip) {
      return
    }

    const place = () => {
      const tab = strip.querySelector<HTMLElement>(`[data-tab-key="${CSS.escape(selectedKey)}"]`)

      if (!tab) {
        setPlacement(null)
        return
      }

      const stripRect = strip.getBoundingClientRect()
      const tabRect = tab.getBoundingClientRect()

      setPlacement({ left: tabRect.left - stripRect.left, width: tabRect.width })
    }

    place()

    const observer = new ResizeObserver(place)

    observer.observe(strip)
    strip.querySelectorAll('[data-tab-key]').forEach((tab) => observer.observe(tab))

    return () => observer.disconnect()
  }, [isSegmented, itemsCount, selectedKey])

  useLayoutEffect(() => {
    if (!placement || isSliding) {
      return
    }

    const frame = requestAnimationFrame(() => setIsSliding(true))

    return () => cancelAnimationFrame(frame)
  }, [isSliding, placement])

  return { stripRef, placement, isSliding }
}

export const TabStrip = <TKey extends string = string>({
  items,
  selectedKey,
  onSelect,
  className,
  appearance = 'underline',
  trailing,
}: ITabStripProps<TKey>) => {
  const isSegmented = appearance === 'segmented'
  const { stripRef, placement, isSliding } = useThumbPlacement(
    isSegmented,
    selectedKey,
    items.length
  )

  return (
    <div
      ref={stripRef}
      className={cn(styles.tabStrip, styles[appearance], className)}
      role="tablist"
    >
      {isSegmented && placement && (
        <span
          className={cn(styles.thumb, { [styles.thumbSliding]: isSliding })}
          style={{ width: placement.width, transform: `translateX(${placement.left}px)` }}
          aria-hidden="true"
        />
      )}

      {items.map((item) => {
        const isSelected = item.key === selectedKey
        /* On the filled strip the selected tab speaks in one accent colour, subtitle included, so
           the item's own subtitle colour steps aside. */
        const accentSubtitle = appearance === 'filled' && isSelected
        const isAllClear = !item.count && (!item.tone || item.tone === 'success')

        const tab = (
          <div
            key={item.key}
            data-tab-key={item.key}
            className={cn(styles.tab, {
              [styles.selected]: isSelected,
              [styles.muted]: item.muted,
              [styles.disabled]: item.disabled,
              [styles[`tone-${item.tone}`]]: Boolean(item.tone),
            })}
          >
            <button
              type="button"
              role="tab"
              aria-selected={isSelected}
              disabled={item.disabled}
              onClick={() => onSelect(item.key)}
              className={styles.tabButton}
            >
              {item.icon && <span className={styles.icon}>{item.icon}</span>}

              <span className={styles.tabContent}>
                <span className={styles.title}>
                  {typeof item.title === 'string' ? (
                    <Translation text={item.title} span />
                  ) : (
                    item.title
                  )}
                </span>

                {item.secondaryContent && (
                  <span className={styles.secondaryContent}>{item.secondaryContent}</span>
                )}

                {isSegmented && !isAllClear && item.tone && !item.muted && !item.loading && (
                  <span
                    className={cn(styles.dot, { [styles.countBadge]: Boolean(item.count) })}
                    aria-hidden="true"
                  >
                    {item.count || null}
                  </span>
                )}

                {isSegmented && isAllClear && !item.muted && !item.loading && (
                  <span
                    className={cn(styles.checkBadge, { [styles.unfinishedBadge]: item.unfinished })}
                    aria-hidden="true"
                  >
                    {item.unfinished ? <ExclamationIcon /> : <CheckIcon />}
                  </span>
                )}

                {isSegmented && item.loading && (
                  <span className={styles.spinner} aria-hidden="true" />
                )}

                {isSegmented && item.muted && !item.loading && (
                  <span className={styles.mutedMark} title={item.mutedLabel}>
                    <EyeOffIcon />
                    {item.mutedLabel && <span className={styles.srOnly}>{item.mutedLabel}</span>}
                  </span>
                )}

                {item.subtitle && (
                  <span
                    className={styles.subtitle}
                    style={{ color: accentSubtitle ? undefined : item.subtitleColor }}
                  >
                    {item.subtitle}
                  </span>
                )}
              </span>
            </button>

            {item.adornment && <span className={styles.adornment}>{item.adornment}</span>}

            {item.cornerAction && <span className={styles.corner}>{item.cornerAction}</span>}
          </div>
        )

        return item.tooltip ? (
          <Tooltip
            key={item.key}
            className={styles.tooltipAnchor}
            tipContent={item.tooltip}
            verticalDirection={TPopupVertical.Bottom}
            horizontalDirection={TPopupHorizontal.Right}
            textAlign="left"
          >
            {tab}
          </Tooltip>
        ) : (
          tab
        )
      })}

      {trailing && (
        <span className={styles.trailing} role="none">
          {trailing}
        </span>
      )}
    </div>
  )
}
